import { useEffect, useState, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { motion } from "framer-motion";
import { MapPin, Navigation, Route, Clock, Calendar, Car, ExternalLink, Ticket, Download, Sparkles, Plane } from "lucide-react";

const routeStops = [
  { name: "Las Vegas – Bellagio", state: "Nevada", lat: 36.1126, lng: -115.1767, day: "1-3", color: "#E76F51", dates: "17.-20. Juli", address: "Bellagio Hotel, 3600 S Las Vegas Blvd, Las Vegas, NV 89109", phone: "888-987-6667", checkin: "15:00", booking: "77L0ZCMSRS" },
  { name: "Los Angeles – Marina Escape", state: "Kalifornien", lat: 33.9802, lng: -118.4517, day: "4-6", color: "#E9C46A", dates: "20.-23. Juli", address: "Marina del Rey, Los Angeles, CA 90292", phone: "+1 818-388-2847", checkin: "15:00", booking: "6920124976" },
  { name: "Sequoia NP – Wuksachi Lodge", state: "Kalifornien", lat: 36.4864, lng: -118.5658, day: "7-8", color: "#2A9D8F", dates: "23.-25. Juli", address: "Wuksachi Lodge, 64740 Wuksachi Way, Sequoia NP, CA 93262", booking: "404SF358498" },
  { name: "Yosemite NP – The Ahwahnee", state: "Kalifornien", lat: 37.7468, lng: -119.5748, day: "9-10", color: "#2A9D8F", dates: "25.-27. Juli", address: "The Ahwahnee, 1 Ahwahnee Dr, Yosemite National Park, CA", checkin: "16:00", booking: "16K3CM" },
  { name: "San Francisco – Argonaut Hotel", state: "Kalifornien", lat: 37.8080, lng: -122.4177, day: "11-12", color: "#E9C46A", dates: "27.-29. Juli", address: "Argonaut Hotel, 495 Jefferson St, San Francisco, CA 94109", booking: "5018972355" },
  { name: "Benbow KOA Holiday", state: "Kalifornien", lat: 40.0688, lng: -123.7893, day: "13", color: "#2A9D8F", dates: "29.-30. Juli", accom: "Wohnmobil-Stellplatz", address: "7000 Benbow Drive, Garberville, CA 95542", phone: "1-707-923-2777", checkin: "13:00", booking: "21208357" },
  { name: "Crescent City KOA Holiday", state: "Kalifornien", lat: 41.7876, lng: -124.1637, day: "14-15", color: "#2A9D8F", dates: "30. Jul-1. Aug", accom: "Wohnmobil-Stellplatz (Redwoods)", address: "4241 Highway 101 North, Crescent City, CA 95531", phone: "1-707-464-5744", checkin: "14:00" },
  { name: "Bullards Beach State Park", state: "Oregon", lat: 43.1541, lng: -124.3952, day: "16", color: "#2A9D8F", dates: "1.-2. Aug", accom: "Campsite A02 (Wohnmobil)", address: "Bullards Beach State Park, Bandon, OR 97411", booking: "2-38248479" },
  { name: "Waldport / Newport KOA", state: "Oregon", lat: 44.4268, lng: -124.0716, day: "17", color: "#2A9D8F", dates: "2.-3. Aug", accom: "Wohnmobil-Stellplatz", address: "1330 NW Pacific Coast Highway, Waldport, OR 97394", phone: "1-800-562-3443", checkin: "14:00" },
  { name: "Astoria KOA Resort", state: "Oregon", lat: 46.1879, lng: -123.9495, day: "18", color: "#2A9D8F", dates: "3.-4. Aug", accom: "Wohnmobil-Stellplatz", address: "1100 Northwest Ridge Road, Hammond, OR 97121", phone: "1-503-861-2606", checkin: "15:00" },
  { name: "Quileute – La Push", state: "Washington", lat: 47.9076, lng: -124.6353, day: "19", color: "#264653", dates: "4.-5. Aug", accom: "Quileute Oceanside Resort", address: "330 Ocean Front Drive, La Push, WA 98350", booking: "2026018118" },
  { name: "Port Angeles – Crescent Beach", state: "Washington", lat: 48.1614, lng: -123.7211, day: "20-21", color: "#2A9D8F", dates: "5.-7. Aug", accom: "Crescent Beach & RV Park", address: "2860 Crescent Beach Road, Port Angeles, WA 98363", phone: "360-928-3344", checkin: "15:00", booking: "269599" },
  { name: "Seattle – Sound Hotel", state: "Washington", lat: 47.6133, lng: -122.3467, day: "22", color: "#E76F51", dates: "7.-8. Aug", accom: "The Sound Hotel Belltown", address: "2120 4th Avenue, Seattle, WA 98121", phone: "+1 206-441-7456", checkin: "16:00", booking: "6917243789" },
  { name: "Leavenworth – Berg Haus", state: "Washington", lat: 47.5962, lng: -120.6615, day: "23-24", color: "#F4A261", dates: "8.-10. Aug", address: "18800 Beaver Valley Rd, Leavenworth, WA 98826", phone: "509-763-0180" },
  { name: "Seattle – Hotel 1000", state: "Washington", lat: 47.6050, lng: -122.3380, day: "25", color: "#E76F51", dates: "10.-11. Aug", accom: "Hotel 1000, Hyatt Unbound", address: "1000 1st Avenue, Seattle, WA 98104", phone: "+1 206-957-1000", checkin: "16:00", booking: "6369914287" },
];

const dailyHighlights = [
  { day: 1, date: "17. Juli", title: "Las Vegas - Ankunft!", color: "#E76F51", text: "Lieber Levi, Louie und Noah! Heute landen wir in Las Vegas - der verrücktesten Stadt der Welt! Überall blinkt und leuchtet es. Unser Hotel, das Bellagio, hat einen riesigen Springbrunnen davor, der zur Musik tanzt. Abends machen wir einen Spaziergang über den berühmten Las Vegas Strip - haltet die Augen auf, hier gibt es eine Pyramide, den Eiffelturm und sogar Venedig mit Gondeln!" },
  { day: 2, date: "18. Juli", title: "The Sphere - Wizard of Oz!", color: "#E76F51", text: "Lieber Levi, Louie und Noah! Heute erwartet uns etwas ganz Besonderes: Wir gehen in die Sphere! Das ist die größte Kugel der Welt - von außen leuchtet sie wie ein riesiger Bildschirm. Drinnen schauen wir uns den Wizard of Oz an, auf dem größten und schärfsten Bildschirm, den es gibt. Doors open um 10:15, die Show startet um 11:00 Uhr. Das wird wie im Film sitzen - nur 1000-mal cooler!", excursion: "The Sphere - Wizard of Oz (11:00 Uhr, Doors 10:15)" },
  { day: 3, date: "19. Juli", title: "Grand Canyon Helikopter!", color: "#E76F51", text: "Lieber Levi, Louie und Noah! Heute wird LEGENDÄR: Wir fliegen mit dem Helikopter über den Grand Canyon! Aufstehen lohnt sich - Abfahrt ist um 7:00 Uhr morgens. Der Grand Canyon ist 1.800 Meter tief und bis zu 29 km breit. Aus der Luft sieht man die bunten Gesteinsschichten, die über Millionen Jahre entstanden sind. Haltet eure Kameras bereit!", excursion: "Grand Canyon Helikopter-Tour (07:00 Uhr)" },
  { day: 4, date: "20. Juli", title: "Los Angeles - Hollywood!", color: "#E9C46A", text: "Lieber Levi, Louie und Noah! Heute fahren wir nach Los Angeles - die Stadt der Filmstars! Unser Apartment liegt in Marina del Rey direkt am Hafen mit Blick aufs Meer. In LA könnt ihr den Hollywood-Schriftzug sehen, über den Walk of Fame laufen und die Handabdrücke berühmter Stars anschauen. Welchen Star wollt ihr am meisten finden?" },
  { day: 5, date: "21. Juli", title: "Universal Studios VIP!", color: "#E9C46A", text: "Lieber Levi, Louie und Noah! Heute erwartet uns die Universal Studios Hollywood VIP Experience! Wir bekommen eine private Tour hinter die Kulissen, sehen wie Filme gemacht werden, und dürfen auf ALLE Fahrgeschäfte - ohne anstehen! Von Harry Potter über Jurassic World bis zu den Minions. Als VIPs bekommen wir sogar ein eigenes Mittagessen. Das wird der beste Freizeitpark-Tag ever!", excursion: "Universal Studios Hollywood - VIP Experience (ganztägig)" },
  { day: 6, date: "22. Juli", title: "Los Angeles - Strand & Venice Beach", color: "#E9C46A", text: "Lieber Levi, Louie und Noah! Heute genießen wir LA am Strand! Venice Beach ist berühmt für seine bunten Straßenkünstler, den Muscle Beach (wo starke Männer und Frauen trainieren) und den langen Boardwalk. Wir können Fahrrad fahren, Skatern zuschauen und Eis essen. Am Santa Monica Pier gibt es einen kleinen Freizeitpark direkt am Meer!" },
  { day: 7, date: "23. Juli", title: "Sequoia Nationalpark!", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute fahren wir in den Sequoia Nationalpark zu den größten Bäumen der WELT! Der General Sherman Tree ist der größte lebende Baum - er ist 84 Meter hoch und über 2.000 Jahre alt. Sein Stamm ist so dick, dass 20 Kinder ihn kaum umarmen könnten! Unser Hotel, die Wuksachi Lodge, liegt mitten im Wald zwischen den Riesenbäumen." },
  { day: 8, date: "24. Juli", title: "Sequoia - Wandern bei den Riesen", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute erkunden wir Sequoia weiter! Wandert durch den Congress Trail und entdeckt noch mehr Mammutbäume. Am Moro Rock könnt ihr 400 Stufen hochsteigen und habt dann einen atemberaubenden Blick über die Sierra Nevada. Haltet Ausschau nach Schwarzbären, Hirschen und Murmeltieren - die leben hier wirklich!" },
  { day: 9, date: "25. Juli", title: "Yosemite - The Ahwahnee!", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute erwartet uns der Yosemite Nationalpark - einer der schönsten Orte der Welt! Unser Hotel, The Ahwahnee, ist ein historisches Luxushotel aus dem Jahr 1927. Von hier aus seht ihr den El Capitan, eine 1.000 Meter hohe senkrechte Felswand! Die Yosemite Falls sind die höchsten Wasserfälle Nordamerikas - 739 Meter tief!" },
  { day: 10, date: "26. Juli", title: "Yosemite - Half Dome & Wasserfälle", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute geht es weiter durch Yosemite! Der Half Dome sieht aus wie ein Berg, der in der Mitte durchgeschnitten wurde. Am Mirror Lake könnt ihr die Berge kopfüber im Wasser sehen. Im Yosemite Valley gibt es auch Waschbären und wenn ihr Glück habt, seht ihr Kojoten. Vergesst nicht: Junior Ranger Heft ausfüllen und das Abzeichen holen!" },
  { day: 11, date: "27. Juli", title: "San Francisco - Golden Gate!", color: "#E9C46A", text: "Lieber Levi, Louie und Noah! Heute fahren wir nach San Francisco! Die Golden Gate Bridge ist 2.737 Meter lang und in einem speziellen Orange-Rot gestrichen. Unser Hotel, das Argonaut am Fisherman's Wharf, liegt direkt am Wasser. Hier könnt ihr Seelöwen auf den Docks beobachten, frische Clam Chowder in Sauerteigbrot essen und mit dem berühmten Cable Car fahren!" },
  { day: 12, date: "28. Juli", title: "Alcatraz Island!", color: "#E9C46A", text: "Lieber Levi, Louie und Noah! Heute erwartet uns Alcatraz - das berühmteste Gefängnis der Welt! Unsere Fähre fährt um 10:35 Uhr von Pier 33 ab (seid um 10:05 da!). Auf der Insel hört ihr mit dem Audio-Guide die Geschichten der Gefangenen. Wusstet ihr, dass 1962 drei Häftlinge durch einen selbst gegrabenen Tunnel geflohen sind? Bis heute weiß niemand, ob sie überlebt haben!", excursion: "Alcatraz Island Tour (Fähre 10:35 ab Pier 33)" },
  { day: 13, date: "29. Juli", title: "Wohnmobil-Abenteuer startet!", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Ab heute wird es richtig abenteuerlich: Wir holen unser Wohnmobil in San Francisco ab! Ab jetzt sind wir echte Road-Tripper. Heute fahren wir zum Benbow KOA in den Redwoods. Stellt euch vor: Bäume, die höher sind als ein 30-stöckiges Hochhaus! Unser erstes Lagerfeuer im Wohnmobil - Marshmallows nicht vergessen!" },
  { day: 14, date: "30. Juli", title: "Crescent City - Redwood NP!", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute fahren wir durch die Avenue of the Giants - eine 50 km lange Straße durch uralte Mammutbäume! Dann weiter zum Crescent City KOA direkt am Redwood National Park. Hier gibt es einen Drive-Through Tree - ja wirklich, ihr fahrt MIT DEM WOHNMOBIL durch einen lebenden Baum! Am Battery Point Lighthouse könnt ihr bei Ebbe zur Leuchtturm-Insel laufen." },
  { day: 15, date: "31. Juli", title: "Crescent City - Strand & Natur", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute erkunden wir die Gegend um Crescent City! In den Redwoods leben Bananenschnecken - die sind knallgelb und können 25 cm lang werden. Am Strand von Crescent City könnt ihr Seesterne und Seeanemonen in den Gezeitenpools entdecken. Abends gibt es Lagerfeuer auf dem Campingplatz - wer kann die beste Gruselgeschichte erzählen?" },
  { day: 16, date: "1. August", title: "Oregon-Küste - Bullards Beach!", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute fahren wir die wilde Oregon-Küste hoch zum Bullards Beach State Park bei Bandon! Oregon hat die schönsten Felsformationen im Pazifik - riesige Felsen stehen wie Skulpturen im Meer. Am Coquille River Lighthouse könnt ihr einen echten Leuchtturm von innen sehen. Am Strand von Bandon gibt es die berühmten Face Rock Felsen - erkennt ihr das Gesicht?" },
  { day: 17, date: "2. August", title: "Waldport - Oregon Coast Aquarium!", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute erwartet uns Waldport an der Oregon-Küste! In der Nähe in Newport gibt es das Oregon Coast Aquarium - dort lauft ihr durch einen Unterwassertunnel mit Haien über euch! Die Küste hier hat fantastische Gezeitenpools voller Seesterne, Krebse und Seeigel. Abends am KOA Campingplatz könnt ihr den Sonnenuntergang über dem Pazifik beobachten." },
  { day: 18, date: "3. August", title: "Astoria - Goonies-Stadt!", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute fahren wir nach Astoria - die älteste Stadt westlich der Rocky Mountains! Hier wurde der Film 'Die Goonies' gedreht. Die Astoria Column hat 164 Stufen und von oben seht ihr den Columbia River, den Pazifik und die Berge. An der Astoria Column könnt ihr kleine Holzflieger von oben fliegen lassen! Am KOA Resort gibt es einen Pool und Spielplatz." },
  { day: 19, date: "4. August", title: "La Push - Twilight-Strand!", color: "#264653", text: "Lieber Levi, Louie und Noah! Heute fahren wir nach La Push im Quileute-Reservat! Dieser Strand war in den Twilight-Filmen zu sehen. Riesige Felsen ragen aus dem Meer, Treibholz liegt überall - es sieht aus wie in einem Fantasy-Film! Das Quileute-Volk lebt hier seit Tausenden von Jahren. Am First Beach könnt ihr durch die Sea Stacks wandern - das sind die coolen Felsen im Wasser." },
  { day: 20, date: "5. August", title: "Olympic NP - Regenwald!", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute erwartet uns der Hoh Rainforest im Olympic Nationalpark! Hier regnet es bis zu 4 Meter pro Jahr und alles ist mit dickem, grünem Moos bedeckt - wie in einem Märchenwald. Die Bäume tragen 'Bärte' aus Moos und Farn. Haltet Ausschau nach Roosevelt-Elchen - die sind riesig! Unser Camp in Port Angeles liegt am wunderschönen Crescent Beach." },
  { day: 21, date: "6. August", title: "Olympic NP - Berge & Strände", color: "#2A9D8F", text: "Lieber Levi, Louie und Noah! Heute erkunden wir mehr vom Olympic NP! Fahrt hoch zum Hurricane Ridge auf 1.598 Meter - hier seht ihr schneebedeckte Gipfel und vielleicht Murmeltiere und Bergziegen. Am Ruby Beach könnt ihr Skulpturen aus Treibholz bauen. Olympic NP ist einzigartig: er hat Regenwald, Berge UND Strände in einem einzigen Park!" },
  { day: 22, date: "7. August", title: "Seattle - Emerald City!", color: "#E76F51", text: "Lieber Levi, Louie und Noah! Heute fahren wir nach Seattle und geben unser Wohnmobil ab (El Monte RV, 1541 S 96th St). Seattle heißt 'Emerald City' wegen der vielen grünen Bäume! Unser Sound Hotel liegt im hippen Belltown-Viertel. Findet ihr den allerersten Starbucks der Welt? Er ist am Pike Place Market - dem ältesten Bauernmarkt der USA, wo Fischverkäufer mit riesigen Lachsen werfen!", excursion: "Wohnmobil-Rückgabe bei El Monte RV" },
  { day: 23, date: "8. August", title: "Leavenworth - Little Bavaria!", color: "#F4A261", text: "Lieber Levi, Louie und Noah! Heute erwartet uns etwas total Verrücktes: ein bayerisches Dorf mitten in Amerika! Leavenworth sieht aus wie Garmisch - mit Fachwerkhäusern, Biergärten und Brezeln. Unser Berg Haus hat eine eigene Feuerstelle und einen Grill. Könnt ihr 5 Unterschiede zu einem echten deutschen Dorf finden? Hier gibt es sogar ein Nussknacker-Museum!" },
  { day: 24, date: "9. August", title: "Leavenworth - Cascade Mountains", color: "#F4A261", text: "Lieber Levi, Louie und Noah! Heute genießen wir die Cascade Mountains rund um Leavenworth! Wir können am Wenatchee River Tubing gehen (auf großen Reifen den Fluss runtertreiben), wandern gehen oder einfach die Berge bestaunen. Abends grillen wir am Berg Haus - mit Blick auf die Berggipfel. Wie viele Gipfel könnt ihr zählen?" },
  { day: 25, date: "10. August", title: "Seattle - Space Needle!", color: "#E76F51", text: "Lieber Levi, Louie und Noah! Heute erwartet uns die Space Needle in Seattle! Sie ist 184 Meter hoch und wurde für die Weltausstellung 1962 gebaut. Von oben seht ihr den Mount Rainier, den Puget Sound und die ganze Stadt. Unser Hotel 1000 ist super modern - das Bad hat sogar einen Spiegel, der nicht beschlägt! Am Chihuly Garden seht ihr riesige bunte Glasskulpturen." },
  { day: 26, date: "11. August", title: "Heimreise!", color: "#E76F51", text: "Lieber Levi, Louie und Noah! Heute geht unsere große Reise zu Ende. Wir fliegen ca. 9.500 km zurück nach Deutschland - das dauert etwa 10 Stunden. Zeit, um im Flugzeug über die coolsten Erlebnisse nachzudenken: Was war euer absolutes Highlight? Der Helikopter über den Grand Canyon? Die Universal Studios VIP Tour? Die riesigen Redwood-Bäume? Oder der Regenwald im Olympic NP? Eins ist sicher: Das war eine Reise, die ihr nie vergessen werdet!" },
];

// Create a numbered circle marker icon
function createCircleIcon(color, label) {
  const size = 28;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1}" fill="${color}" stroke="white" stroke-width="2"/>
      <text x="50%" y="50%" text-anchor="middle" dy=".35em" fill="white" font-size="11" font-weight="bold" font-family="sans-serif">${label}</text>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

// Helper component to fit bounds on mount and handle flyTo
function MapController({ mapRef, flyTarget, onFlyDone }) {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;
    const bounds = L.latLngBounds(routeStops.map((s) => [s.lat, s.lng]));
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [map, mapRef]);

  useEffect(() => {
    if (flyTarget) {
      map.flyTo([flyTarget.lat, flyTarget.lng], 8, { duration: 0.8 });
      onFlyDone();
    }
  }, [flyTarget, map, onFlyDone]);

  return null;
}

const RoutePage = () => {
  const [selectedStop, setSelectedStop] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const mapRef = useRef(null);

  const polylinePositions = useMemo(
    () => routeStops.map((s) => [s.lat, s.lng]),
    []
  );

  const scrollToStop = (stop) => {
    setSelectedStop(stop);
    setFlyTarget(stop);
  };

  // Approximate total distance using haversine
  const { totalDistance, totalDuration } = useMemo(() => {
    let totalKm = 0;
    for (let i = 0; i < routeStops.length - 1; i++) {
      const a = routeStops[i];
      const b = routeStops[i + 1];
      const R = 6371;
      const dLat = ((b.lat - a.lat) * Math.PI) / 180;
      const dLng = ((b.lng - a.lng) * Math.PI) / 180;
      const sinLat = Math.sin(dLat / 2);
      const sinLng = Math.sin(dLng / 2);
      const h =
        sinLat * sinLat +
        Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * sinLng * sinLng;
      totalKm += R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    }
    // Rough road factor ~1.3x straight-line distance
    const roadKm = Math.round(totalKm * 1.3);
    // Assume ~80 km/h average
    const hours = Math.round(roadKm / 80);
    return {
      totalDistance: `~${roadKm.toLocaleString("de-DE")} km`,
      totalDuration: `~${hours} Std.`,
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="route-page">
      {/* Header */}
      <div className="bg-[#264653] text-white px-4 py-5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <Route className="w-4 h-4 text-[#E9C46A]" />
            <span className="font-nunito text-xs text-white/70">USA Westkuste 2026</span>
          </div>
          <h1 className="font-fraunces text-2xl sm:text-3xl font-bold" data-testid="route-title">
            Unsere Route
          </h1>
          <div className="flex flex-wrap gap-4 mt-2 text-sm">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 text-[#E9C46A]" /> 17. Juli - 11. August
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-[#E9C46A]" /> {routeStops.length} Stopps
            </span>
            <span className="flex items-center gap-1">
              <Car className="w-3.5 h-3.5 text-[#E9C46A]" /> {totalDistance} &middot; {totalDuration}
            </span>
          </div>
          <button
            onClick={() => {
              const origin = `${routeStops[0].lat},${routeStops[0].lng}`;
              const dest = `${routeStops[routeStops.length - 1].lat},${routeStops[routeStops.length - 1].lng}`;
              const waypoints = routeStops.slice(1, -1).map(s => `${s.lat},${s.lng}`).join("|");
              window.open(`https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&waypoints=${waypoints}&travelmode=driving`, "_blank");
            }}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-full text-xs font-semibold transition-colors"
            data-testid="export-google-maps"
          >
            <ExternalLink className="w-3.5 h-3.5" /> In Google Maps offnen
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="px-4 py-2.5 bg-white border-b border-[#E0E0D0]">
        <div className="max-w-4xl mx-auto flex flex-wrap gap-3 text-xs">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#E76F51]" /> Start/Ende</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#2A9D8F]" /> Nationalparks</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#E9C46A]" /> Stadte</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#F4A261]" /> Leavenworth</span>
        </div>
      </div>

      {/* Map */}
      <div className="h-[45vh] sm:h-[55vh]" data-testid="leaflet-map">
        <MapContainer
          center={[39.5, -119.5]}
          zoom={5}
          style={{ width: "100%", height: "100%" }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapController
            mapRef={mapRef}
            flyTarget={flyTarget}
            onFlyDone={() => setFlyTarget(null)}
          />

          {/* Route polyline */}
          <Polyline
            positions={polylinePositions}
            pathOptions={{ color: "#264653", opacity: 0.85, weight: 4 }}
          />

          {/* Main stop markers */}
          {routeStops.map((stop, idx) => (
            <Marker
              key={stop.name}
              position={[stop.lat, stop.lng]}
              icon={createCircleIcon(stop.color, String(idx + 1))}
              eventHandlers={{ click: () => setSelectedStop(stop) }}
            >
              <Popup>
                <div className="p-1.5 min-w-[140px]">
                  <h3 className="font-bold text-[#264653] text-base">{stop.name}</h3>
                  <p className="text-xs text-[#8D99AE]">{stop.state}</p>
                  <div className="flex items-center gap-1 mt-1.5 text-xs">
                    <Clock className="w-3.5 h-3.5 text-[#2A9D8F]" />
                    <span className="text-[#264653]">{stop.dates || `Tag ${stop.day}`}</span>
                  </div>
                  {stop.accom && (
                    <p className="mt-1.5 text-[10px] text-[#2A9D8F] leading-tight">{stop.accom}</p>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}

        </MapContainer>
      </div>

      {/* Route List */}
      <div className="px-4 py-5">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-fraunces text-lg font-bold text-[#264653] mb-3">Reiseverlauf</h2>

          <div className="space-y-1.5">
            {routeStops.map((stop, idx) => (
              <motion.div
                key={stop.name}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                  selectedStop?.name === stop.name
                    ? "bg-[#264653]/5 border-[#264653]/30"
                    : "bg-white border-[#E0E0D0] hover:bg-[#F0EFEB]"
                }`}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.04 }}
                data-testid={`route-stop-${idx}`}
              >
                <button
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                  style={{ backgroundColor: stop.color }}
                  onClick={() => scrollToStop(stop)}
                >
                  {idx + 1}
                </button>
                <button className="flex-1 min-w-0 text-left" onClick={() => scrollToStop(stop)}>
                  <h3 className="font-nunito font-semibold text-sm text-[#264653]">{stop.name}</h3>
                  <p className="text-[11px] text-[#8D99AE]">{stop.state}</p>
                  {stop.accom && (
                    <p className="text-[10px] text-[#2A9D8F] mt-0.5">{stop.accom}</p>
                  )}
                  {stop.phone && (
                    <p className="text-[10px] text-[#8D99AE] mt-0.5">Check-in: {stop.checkin} Uhr</p>
                  )}
                </button>
                <div className="text-right flex-shrink-0 flex flex-col items-end gap-1.5">
                  <span className="font-nunito text-xs font-medium text-[#264653]">{stop.dates}</span>
                  <div className="text-[10px] text-[#8D99AE]">Tag {stop.day}</div>
                  <div className="flex gap-1.5">
                    {stop.phone && (
                      <a
                        href={`tel:${stop.phone}`}
                        className="flex items-center gap-1 px-2 py-1 bg-[#264653]/10 hover:bg-[#264653]/20 rounded-full text-[10px] font-semibold text-[#264653] transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>Anrufen</span>
                      </a>
                    )}
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stop.address)}&travelmode=driving`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2 py-1 bg-[#2A9D8F] hover:bg-[#238577] rounded-full text-[10px] font-semibold text-white transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Navigation className="w-3 h-3" />
                      <span>Navi</span>
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}

          </div>

          {/* Daily Highlights */}
          <h2 className="font-fraunces text-lg font-bold text-[#264653] mt-8 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#E9C46A]" />
            Tageshighlights
          </h2>
          <div className="space-y-1.5 mb-8">
            {dailyHighlights.map((h) => (
              <motion.div
                key={h.day}
                className="bg-white border border-[#E0E0D0] rounded-xl overflow-hidden"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: h.day * 0.02 }}
              >
                <button
                  className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#F0EFEB] transition-colors"
                  onClick={() => setExpandedDay(expandedDay === h.day ? null : h.day)}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                    style={{ backgroundColor: h.color }}
                  >
                    {h.day}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-nunito font-semibold text-sm text-[#264653]">{h.title}</h3>
                    <p className="text-[11px] text-[#8D99AE]">{h.date}</p>
                  </div>
                  {h.excursion && (
                    <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 bg-[#E9C46A]/20 text-[#264653] text-[10px] rounded-full font-semibold flex-shrink-0">
                      <Ticket className="w-3 h-3" /> Ausflug
                    </span>
                  )}
                  <span className="text-[#8D99AE] text-sm flex-shrink-0">
                    {expandedDay === h.day ? "▲" : "▼"}
                  </span>
                </button>
                {expandedDay === h.day && (
                  <div className="px-4 pb-4 pt-1 border-t border-[#E0E0D0]/50">
                    <p className="text-sm text-[#264653] leading-relaxed">{h.text}</p>
                    {h.excursion && (
                      <div className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-[#E9C46A]/15 rounded-lg text-xs text-[#264653] font-semibold">
                        <Ticket className="w-3.5 h-3.5 text-[#E9C46A]" />
                        {h.excursion}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Activities & Tickets */}
          <h2 className="font-fraunces text-lg font-bold text-[#264653] mt-8 mb-3 flex items-center gap-2">
            <Ticket className="w-5 h-5 text-[#E9C46A]" />
            Aktivitäten & Tickets
          </h2>
          <div className="space-y-3">
            {/* Sphere - Wizard of Oz */}
            <div className="bg-white border border-[#E0E0D0] rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E76F51] flex items-center justify-center text-white flex-shrink-0">
                  <span className="text-lg">🎭</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-nunito font-semibold text-sm text-[#264653]">The Sphere – Wizard of Oz</h3>
                  <p className="text-[11px] text-[#8D99AE]">Las Vegas, Nevada</p>
                  <div className="flex flex-wrap gap-2 mt-1.5 text-[10px]">
                    <span className="flex items-center gap-1 text-[#2A9D8F]"><Calendar className="w-3 h-3" /> 18. Juli 2026</span>
                    <span className="flex items-center gap-1 text-[#264653]"><Clock className="w-3 h-3" /> 11:00 Uhr (Doors 10:15)</span>
                  </div>
                  <p className="text-[10px] text-[#8D99AE] mt-1">Tag 2 · Booking: via Sphere</p>
                </div>
              </div>
            </div>

            {/* Grand Canyon Helikopter */}
            <div className="bg-white border border-[#E0E0D0] rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E76F51] flex items-center justify-center text-white flex-shrink-0">
                  <span className="text-lg">🚁</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-nunito font-semibold text-sm text-[#264653]">Grand Canyon Helikopter-Tour</h3>
                  <p className="text-[11px] text-[#8D99AE]">Las Vegas, Nevada</p>
                  <div className="flex flex-wrap gap-2 mt-1.5 text-[10px]">
                    <span className="flex items-center gap-1 text-[#2A9D8F]"><Calendar className="w-3 h-3" /> 19. Juli 2026</span>
                    <span className="flex items-center gap-1 text-[#264653]"><Clock className="w-3 h-3" /> 07:00 Uhr</span>
                  </div>
                  <p className="text-[10px] text-[#8D99AE] mt-1">Tag 3</p>
                </div>
              </div>
            </div>

            {/* Universal Studios VIP */}
            <div className="bg-white border border-[#E0E0D0] rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E9C46A] flex items-center justify-center text-white flex-shrink-0">
                  <span className="text-lg">🎬</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-nunito font-semibold text-sm text-[#264653]">Universal Studios Hollywood – VIP Experience</h3>
                  <p className="text-[11px] text-[#8D99AE]">Los Angeles, Kalifornien</p>
                  <div className="flex flex-wrap gap-2 mt-1.5 text-[10px]">
                    <span className="flex items-center gap-1 text-[#2A9D8F]"><Calendar className="w-3 h-3" /> 21. Juli 2026</span>
                  </div>
                  <p className="text-[10px] text-[#8D99AE] mt-1">Tag 5 · Ticket: GYGVN3HA5MNH-941313-HGYGI (GetYourGuide)</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <a
                      href="/tickets/universal-studios-vip.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#E9C46A] hover:bg-[#d4b35a] rounded-full text-[10px] font-semibold text-white transition-colors"
                    >
                      <Download className="w-3 h-3" /> VIP Confirmation
                    </a>
                    <a
                      href="/tickets/universal-studios-ticket.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#2A9D8F] hover:bg-[#238577] rounded-full text-[10px] font-semibold text-white transition-colors"
                    >
                      <Download className="w-3 h-3" /> Ticket PDF
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Alcatraz */}
            <div className="bg-white border border-[#E0E0D0] rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#264653] flex items-center justify-center text-white flex-shrink-0">
                  <span className="text-lg">🏝️</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-nunito font-semibold text-sm text-[#264653]">Alcatraz Island Tour</h3>
                  <p className="text-[11px] text-[#8D99AE]">San Francisco, Kalifornien</p>
                  <div className="flex flex-wrap gap-2 mt-1.5 text-[10px]">
                    <span className="flex items-center gap-1 text-[#2A9D8F]"><Calendar className="w-3 h-3" /> 28. Juli 2026</span>
                    <span className="flex items-center gap-1 text-[#264653]"><Clock className="w-3 h-3" /> 10:35 Uhr ab Pier 33</span>
                  </div>
                  <p className="text-[10px] text-[#8D99AE] mt-1">Tag 12</p>
                </div>
              </div>
            </div>
          </div>

          {/* Flights */}
          <h2 className="font-fraunces text-lg font-bold text-[#264653] mt-8 mb-3 flex items-center gap-2">
            <Plane className="w-5 h-5 text-[#E9C46A]" />
            Flüge
          </h2>
          <div className="space-y-3">
            {/* Hinflug */}
            <div className="bg-white border border-[#E0E0D0] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 bg-[#2A9D8F]/15 text-[#2A9D8F] text-[9px] font-bold rounded-full">HINFLUG</span>
                <span className="font-nunito text-xs font-medium text-[#264653]">17. Juli 2026</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-2.5 bg-[#F9F9F7] rounded-lg">
                  <div className="text-center flex-shrink-0">
                    <div className="font-nunito font-bold text-sm text-[#264653]">12:10</div>
                    <div className="text-[10px] text-[#8D99AE]">BER</div>
                  </div>
                  <div className="flex-1 flex items-center gap-1">
                    <div className="h-px flex-1 bg-[#E0E0D0]" />
                    <span className="text-[9px] text-[#8D99AE] px-1">1h20</span>
                    <div className="h-px flex-1 bg-[#E0E0D0]" />
                  </div>
                  <div className="text-center flex-shrink-0">
                    <div className="font-nunito font-bold text-sm text-[#264653]">13:30</div>
                    <div className="text-[10px] text-[#8D99AE]">AMS</div>
                  </div>
                </div>
                <p className="text-[10px] text-[#8D99AE] px-1">KLM KL1776 · Airbus A321NEO</p>
                <p className="text-[10px] text-[#2A9D8F] px-1 font-semibold">Transfer Amsterdam: 3h25</p>
                <div className="flex items-center gap-3 p-2.5 bg-[#F9F9F7] rounded-lg">
                  <div className="text-center flex-shrink-0">
                    <div className="font-nunito font-bold text-sm text-[#264653]">16:55</div>
                    <div className="text-[10px] text-[#8D99AE]">AMS</div>
                  </div>
                  <div className="flex-1 flex items-center gap-1">
                    <div className="h-px flex-1 bg-[#E0E0D0]" />
                    <span className="text-[9px] text-[#8D99AE] px-1">10h40</span>
                    <div className="h-px flex-1 bg-[#E0E0D0]" />
                  </div>
                  <div className="text-center flex-shrink-0">
                    <div className="font-nunito font-bold text-sm text-[#264653]">18:35</div>
                    <div className="text-[10px] text-[#8D99AE]">LAS</div>
                  </div>
                </div>
                <p className="text-[10px] text-[#8D99AE] px-1">KLM KL635 · Boeing 777-200</p>
              </div>
            </div>

            {/* Rückflug */}
            <div className="bg-white border border-[#E0E0D0] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 bg-[#E76F51]/15 text-[#E76F51] text-[9px] font-bold rounded-full">RÜCKFLUG</span>
                <span className="font-nunito text-xs font-medium text-[#264653]">11. August 2026</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-2.5 bg-[#F9F9F7] rounded-lg">
                  <div className="text-center flex-shrink-0">
                    <div className="font-nunito font-bold text-sm text-[#264653]">13:30</div>
                    <div className="text-[10px] text-[#8D99AE]">SEA</div>
                  </div>
                  <div className="flex-1 flex items-center gap-1">
                    <div className="h-px flex-1 bg-[#E0E0D0]" />
                    <span className="text-[9px] text-[#8D99AE] px-1">9h40</span>
                    <div className="h-px flex-1 bg-[#E0E0D0]" />
                  </div>
                  <div className="text-center flex-shrink-0">
                    <div className="font-nunito font-bold text-sm text-[#264653]">08:10 +1</div>
                    <div className="text-[10px] text-[#8D99AE]">CDG</div>
                  </div>
                </div>
                <p className="text-[10px] text-[#8D99AE] px-1">Air France AF337 · Airbus A350-900 · Terminal 2E</p>
                <p className="text-[10px] text-[#2A9D8F] px-1 font-semibold">Transfer Paris CDG: 1h55</p>
                <div className="flex items-center gap-3 p-2.5 bg-[#F9F9F7] rounded-lg">
                  <div className="text-center flex-shrink-0">
                    <div className="font-nunito font-bold text-sm text-[#264653]">10:05</div>
                    <div className="text-[10px] text-[#8D99AE]">CDG</div>
                  </div>
                  <div className="flex-1 flex items-center gap-1">
                    <div className="h-px flex-1 bg-[#E0E0D0]" />
                    <span className="text-[9px] text-[#8D99AE] px-1">1h50</span>
                    <div className="h-px flex-1 bg-[#E0E0D0]" />
                  </div>
                  <div className="text-center flex-shrink-0">
                    <div className="font-nunito font-bold text-sm text-[#264653]">11:55</div>
                    <div className="text-[10px] text-[#8D99AE]">BER</div>
                  </div>
                </div>
                <p className="text-[10px] text-[#8D99AE] px-1">Air France AF1734 · Airbus A220-300 · Terminal 2F → Terminal 1</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoutePage;
